import { getCalendars, getLocales, type Calendar, type Locale } from 'expo-localization';
import { Platform } from 'react-native';
import { inferRegionFromHints, probableCurrency } from '../utils/currency';
import {
    buildAuthMessageLocaleMetadata,
    resolveLocaleSnapshot,
    type LocaleSnapshotV1,
} from './i18n/localeSnapshot';

type MeasurementSystem = Locale['measurementSystem'];
type TextDirection = Locale['textDirection'];

export type DetectedClientPreferences = {
    platform: string;
    localeTags: string[];
    primaryLocale: string | null;
    languageTag: string | null;
    languageCode: string | null;
    regionCode: string | null;
    timezone: string | null;
    rawCurrencyCode: string | null;
    preferredCurrency: ReturnType<typeof probableCurrency>;
    measurementSystem: MeasurementSystem;
    uses24hourClock: boolean | null;
    firstWeekday: number | null;
    textDirection: TextDirection | null;
    userAgent: string | null;
};

function normalizeString(value: string | null | undefined): string | null {
    if (typeof value !== 'string') {
        return null;
    }

    const normalizedValue = value.trim();
    return normalizedValue.length > 0 ? normalizedValue : null;
}

function getSafeLocales(): Locale[] {
    try {
        return getLocales();
    } catch {
        return [];
    }
}

function getSafeCalendars(): Calendar[] {
    try {
        return getCalendars();
    } catch {
        return [];
    }
}

function getSafeNavigatorLocales(): string[] {
    try {
        if (typeof navigator === 'undefined') {
            return [];
        }

        const localeCandidates = navigator.languages?.length ? navigator.languages : [navigator.language].filter(Boolean);
        return localeCandidates
            .map((localeTag) => normalizeString(localeTag))
            .filter((localeTag): localeTag is string => localeTag !== null);
    } catch {
        return [];
    }
}

function getSafeTimeZone(): string | null {
    try {
        const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        return normalizeString(timeZone);
    } catch {
        return null;
    }
}

function getSafeUserAgent(): string | null {
    try {
        if (typeof navigator === 'undefined') {
            return null;
        }

        return normalizeString(navigator.userAgent);
    } catch {
        return null;
    }
}

function dedupeLocaleTags(localeTags: Array<string | null | undefined>): string[] {
    return Array.from(new Set(
        localeTags
            .map((localeTag) => normalizeString(localeTag))
            .filter((localeTag): localeTag is string => localeTag !== null)
    ));
}

function parseLanguageCode(localeTag: string | null): string | null {
    const normalizedLocaleTag = normalizeString(localeTag);

    if (!normalizedLocaleTag) {
        return null;
    }

    try {
        const locale = new Intl.Locale(normalizedLocaleTag);
        return normalizeString(locale.language);
    } catch {
        return normalizedLocaleTag.split('-')[0] || null;
    }
}

export function getDetectedClientPreferences(): DetectedClientPreferences {
    const locales = getSafeLocales();
    const calendars = getSafeCalendars();
    const primaryLocale = locales[0];
    const primaryCalendar = calendars[0];
    const navigatorLocales = getSafeNavigatorLocales();
    const localeTags = dedupeLocaleTags([
        ...locales.map((locale) => locale.languageTag),
        ...navigatorLocales,
    ]);
    const resolvedPrimaryLocale = normalizeString(primaryLocale?.languageTag) ?? localeTags[0] ?? null;
    const timezone = normalizeString(primaryCalendar?.timeZone) ?? getSafeTimeZone();
    const regionCode = inferRegionFromHints({
        localeTags,
        regionCode: primaryLocale?.regionCode ?? primaryLocale?.languageRegionCode ?? null,
        timeZone: timezone,
    });
    const rawCurrencyCode = normalizeString(primaryLocale?.currencyCode ?? primaryLocale?.languageCurrencyCode ?? null);
    const preferredCurrency = probableCurrency({
        localeTags,
        regionCode: primaryLocale?.regionCode ?? primaryLocale?.languageRegionCode ?? null,
        timeZone: timezone,
    });

    return {
        platform: Platform.OS,
        localeTags,
        primaryLocale: resolvedPrimaryLocale,
        languageTag: resolvedPrimaryLocale,
        languageCode: normalizeString(primaryLocale?.languageCode) ?? parseLanguageCode(resolvedPrimaryLocale),
        regionCode,
        timezone,
        rawCurrencyCode,
        preferredCurrency,
        measurementSystem: primaryLocale?.measurementSystem ?? null,
        uses24hourClock: primaryCalendar?.uses24hourClock ?? null,
        firstWeekday: primaryCalendar?.firstWeekday ?? null,
        textDirection: primaryLocale?.textDirection ?? null,
        userAgent: getSafeUserAgent(),
    };
}

/**
 * Best-effort client context attached to auth metadata and profile rows.
 *
 * Note: IP addresses are not collected here because client-side code cannot
 * reliably determine the true public IP without external services.
 */
export function getClientSessionMetadata(): Record<string, unknown> {
    const detectedPreferences = getDetectedClientPreferences();
    const metadata: Record<string, unknown> = {
        client_platform: detectedPreferences.platform,
        detection_source: 'expo-localization',
        ...buildAuthMessageLocaleMetadata({
            detectedPrimaryLocale: detectedPreferences.primaryLocale,
        }),
    };

    if (detectedPreferences.timezone) {
        metadata.timezone = detectedPreferences.timezone;
        metadata.detected_timezone = detectedPreferences.timezone;
    }

    if (detectedPreferences.primaryLocale) {
        metadata.locale = detectedPreferences.primaryLocale;
        metadata.detected_primary_locale = detectedPreferences.primaryLocale;
        metadata.detected_language_tag = detectedPreferences.languageTag;
    }

    if (detectedPreferences.languageCode) {
        metadata.detected_language_code = detectedPreferences.languageCode;
    }

    if (detectedPreferences.regionCode) {
        metadata.detected_region_code = detectedPreferences.regionCode;
    }

    if (detectedPreferences.localeTags.length > 0) {
        metadata.detected_locale_tags = detectedPreferences.localeTags;
    }

    if (detectedPreferences.rawCurrencyCode) {
        metadata.detected_raw_currency_code = detectedPreferences.rawCurrencyCode;
    }

    metadata.detected_preferred_currency = detectedPreferences.preferredCurrency;

    if (detectedPreferences.measurementSystem) {
        metadata.detected_measurement_system = detectedPreferences.measurementSystem;
    }

    if (detectedPreferences.uses24hourClock !== null) {
        metadata.detected_uses_24hour_clock = detectedPreferences.uses24hourClock;
    }

    if (detectedPreferences.firstWeekday !== null) {
        metadata.detected_first_weekday = detectedPreferences.firstWeekday;
    }

    if (detectedPreferences.textDirection) {
        metadata.detected_text_direction = detectedPreferences.textDirection;
    }

    if (detectedPreferences.userAgent) {
        metadata.user_agent = detectedPreferences.userAgent;
    }

    return metadata;
}

export function getClientLocaleSnapshot(
    preferredLanguageTag?: string | null
): LocaleSnapshotV1 {
    const detectedPreferences = getDetectedClientPreferences();
    return resolveLocaleSnapshot({
        preferredLanguageTag,
        detectedPrimaryLocale: detectedPreferences.primaryLocale,
    });
}

export function getAuthMessageLocaleMetadata(
    preferredLanguageTag?: string | null,
    existingMessageLocale?: string | null
): Record<'paperzilla_auth_message_locale', string> {
    const detectedPreferences = getDetectedClientPreferences();
    return buildAuthMessageLocaleMetadata({
        preferredLanguageTag,
        detectedPrimaryLocale: detectedPreferences.primaryLocale,
        existingMessageLocale,
    });
}

export function getDeviceFingerprintSeed(): string | null {
    const detectedPreferences = getDetectedClientPreferences();
    const seedParts = [
        detectedPreferences.platform,
        detectedPreferences.timezone,
        detectedPreferences.primaryLocale,
        detectedPreferences.regionCode,
        detectedPreferences.userAgent,
    ]
        .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
        .map((value) => value.trim());

    if (seedParts.length === 0) {
        return null;
    }

    return seedParts.join('|').slice(0, 512);
}

export function getClientTimeZone(): string | null {
    return getDetectedClientPreferences().timezone;
}
